import React, { useState, useEffect, useCallback } from 'react';
import { Zap, Trophy, RefreshCw, ArrowRight, AlertCircle } from 'lucide-react';
import { GlassCard, GlassBadge, GlassButton } from '../ui';
import { getOptimizationDashboard } from '../../services/api';
import { useNavigate } from 'react-router-dom';

interface OptimizationDashboardWidgetProps {
    projectId: string;
}

interface DashboardData {
    running_experiments: number;
    total_experiments: number;
    total_conversion_lift_pct: number;
    recent_winners: Array<{
        experiment_name: string;
        winner_label: string;
        lift_pct: number;
    }>;
}

const OptimizationDashboardWidget: React.FC<OptimizationDashboardWidgetProps> = ({ projectId }) => {
    const navigate = useNavigate();
    const [data, setData] = useState<DashboardData | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const fetchDashboard = useCallback(async () => {
        setLoading(true);
        setError(null);
        try {
            const result = await getOptimizationDashboard(projectId);
            setData(result);
        } catch (err: any) {
            setError(err?.response?.data?.detail || 'Failed to load optimization data');
        } finally {
            setLoading(false);
        }
    }, [projectId]);

    useEffect(() => {
        fetchDashboard();
    }, [fetchDashboard]);

    if (loading) {
        return (
            <GlassCard padding="md" className="optimization-widget">
                <div className="optimization-widget-header">
                    <Zap size={18} style={{ color: 'var(--primary-500)' }} />
                    <h4 className="optimization-widget-title">Adaptive Optimization</h4>
                </div>
                <div className="optimization-widget-loading">
                    <RefreshCw size={18} className="trend-spinner" style={{ color: 'var(--primary-500)' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>Loading...</span>
                </div>
            </GlassCard>
        );
    }

    if (error) {
        return (
            <GlassCard padding="md" className="optimization-widget">
                <div className="optimization-widget-header">
                    <Zap size={18} style={{ color: 'var(--primary-500)' }} />
                    <h4 className="optimization-widget-title">Adaptive Optimization</h4>
                </div>
                <div className="optimization-widget-error">
                    <AlertCircle size={16} style={{ color: 'var(--danger)' }} />
                    <span style={{ color: 'var(--text-secondary)', fontSize: 'var(--text-sm)' }}>{error}</span>
                </div>
            </GlassCard>
        );
    }

    if (!data || (data.total_experiments === 0)) {
        return (
            <GlassCard padding="md" className="optimization-widget">
                <div className="optimization-widget-header">
                    <Zap size={18} style={{ color: 'var(--primary-500)' }} />
                    <h4 className="optimization-widget-title">Adaptive Optimization</h4>
                </div>
                <p style={{ color: 'var(--text-muted)', fontSize: 'var(--text-sm)', margin: 0 }}>
                    No experiments running. Create one to start optimizing your content.
                </p>
                <div className="optimization-widget-footer">
                    <GlassButton
                        variant="secondary"
                        size="sm"
                        icon={<ArrowRight size={14} />}
                        iconPosition="right"
                        onClick={() => navigate('/optimization')}
                    >
                        Get Started
                    </GlassButton>
                </div>
            </GlassCard>
        );
    }

    const recentWinners = (data.recent_winners || []).slice(0, 3);

    return (
        <GlassCard padding="md" className="optimization-widget">
            <div className="optimization-widget-header">
                <Zap size={18} style={{ color: 'var(--primary-500)' }} />
                <h4 className="optimization-widget-title">Adaptive Optimization</h4>
            </div>

            {/* Stats */}
            <div className="optimization-widget-stat-row">
                <span className="optimization-widget-stat-label">Running Experiments</span>
                <span className="optimization-widget-stat-value">{data.running_experiments}</span>
            </div>
            {data.total_conversion_lift_pct !== 0 && (
                <div className="optimization-widget-stat-row">
                    <span className="optimization-widget-stat-label">Total Conversion Lift</span>
                    <GlassBadge
                        variant={data.total_conversion_lift_pct > 0 ? 'success' : 'danger'}
                        size="sm"
                    >
                        {(data.total_conversion_lift_pct || 0) > 0 ? '+' : ''}{(data.total_conversion_lift_pct || 0).toFixed(1)}%
                    </GlassBadge>
                </div>
            )}

            {/* Recent Winners */}
            {recentWinners.length > 0 && (
                <div className="optimization-widget-winners">
                    <h5 style={{
                        margin: 0,
                        fontSize: 'var(--text-xs)',
                        fontWeight: 'var(--font-semibold)',
                        color: 'var(--text-secondary)',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                    }}>
                        Recent Winners
                    </h5>
                    {recentWinners.map((w, idx) => (
                        <div key={idx} className="optimization-widget-winner-row">
                            <Trophy size={14} style={{ color: 'var(--success)', flexShrink: 0 }} />
                            <span className="optimization-widget-winner-name">{w.experiment_name}</span>
                            <span className={`lift-badge ${w.lift_pct > 0 ? 'lift-positive' : 'lift-neutral'}`}>
                                {(w.lift_pct || 0) > 0 ? '+' : ''}{(w.lift_pct || 0).toFixed(1)}%
                            </span>
                        </div>
                    ))}
                </div>
            )}

            {/* View All */}
            <div className="optimization-widget-footer">
                <GlassButton
                    variant="secondary"
                    size="sm"
                    icon={<ArrowRight size={14} />}
                    iconPosition="right"
                    onClick={() => navigate('/optimization')}
                >
                    View All
                </GlassButton>
            </div>
        </GlassCard>
    );
};

export default OptimizationDashboardWidget;
